import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save } from 'lucide-react';
import { Button, Checkbox, EmptyState, ErrorState, FormField, Loading, NumberField, PageHeader, Panel, SearchPanel, Select, TextField, useConfirm, useToast } from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { ApiError, errorMessage } from '@/lib/api';
import { formatNumber } from '@/lib/format';
import { useCodeValues } from '@/features/masters/api';
import { BusinessOperatorSelect } from '@/features/masters/components/BusinessOperatorSelect';
import { usePlanDays, useSavePlanDays, type PlanDaysFilter, type PlanDaysRow } from './api';
import './vessels.css';

type Quarter = 1 | 2 | 3 | 4;
type Edit = { planDaysQ1: number | null; planDaysQ2: number | null; planDaysQ3: number | null; planDaysQ4: number | null };
const Q_KEYS = ['planDaysQ1', 'planDaysQ2', 'planDaysQ3', 'planDaysQ4'] as const;

/** S-13 計画日数登録（基金・漁協） */
export function PlanDaysPage() {
  const navigate = useNavigate();
  const { year, yearPath } = useFiscalYear();
  const toast = useToast();
  const confirm = useConfirm();
  const { data: codes } = useCodeValues();
  const [params, setParams] = useSearchParams();
  const filter: PlanDaysFilter = { businessOperatorId: params.get('businessOperatorId') ?? '', name: params.get('name') ?? '', fishingMethod: params.get('fishingMethod') ?? '' };
  const { data, isPending, error, refetch } = usePlanDays(year, filter);
  const save = useSavePlanDays();
  const { register, handleSubmit, reset, setValue, watch } = useForm<PlanDaysFilter>({ values: filter });

  const [edits, setEdits] = useState<Record<number, Edit>>({});
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [bulk, setBulk] = useState<Record<Quarter, number | null>>({ 1: null, 2: null, 3: null, 4: null });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const rows = useMemo(() => data?.items ?? [], [data]);
  // 検索結果が変わったら編集内容・チェックを初期化
  useEffect(() => {
    setEdits(Object.fromEntries(rows.map((r) => [r.vesselId, { planDaysQ1: r.planDaysQ1, planDaysQ2: r.planDaysQ2, planDaysQ3: r.planDaysQ3, planDaysQ4: r.planDaysQ4 }])));
    setChecked(new Set());
    setErrors({});
  }, [rows]);

  const limit = data?.totalLimitDays ?? null;
  const allChecked = rows.length > 0 && checked.size === rows.length;

  const setQ = (id: number, q: Quarter, v: number | null) => setEdits((e) => ({ ...e, [id]: { ...e[id]!, [Q_KEYS[q - 1]!]: v } }));
  const toggle = (id: number, on: boolean) =>
    setChecked((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  const applyBulk = async (q: Quarter) => {
    const v = bulk[q];
    if (v == null) {
      toast.error('一括入力できません', '一括入力には数値を入れてください。');
      return;
    }
    if (checked.size === 0) {
      toast.error('一括入力できません', 'チェックされた船がありません。');
      return;
    }
    const ok = await confirm({ title: '一括入力しますか？', message: `チェックされた ${checked.size} 隻の第${q}四半期計画日数を ${v} 日にします。\n（「更新」を押すまで保存されません）`, confirmLabel: '入力する' });
    if (!ok) return;
    setEdits((e) => {
      const n = { ...e };
      checked.forEach((id) => (n[id] = { ...n[id]!, [Q_KEYS[q - 1]!]: v }));
      return n;
    });
  };

  const onSave = async () => {
    setErrors({});
    const targets = rows.filter((r) => checked.has(r.vesselId));
    if (targets.length === 0) {
      toast.error('更新できません', 'チェックされた船がありません。');
      return;
    }
    const body = targets.map((r) => {
      const e = edits[r.vesselId]!;
      return { vesselId: r.vesselId, planDaysQ1: e.planDaysQ1 ?? 0, planDaysQ2: e.planDaysQ2 ?? 0, planDaysQ3: e.planDaysQ3 ?? 0, planDaysQ4: e.planDaysQ4 ?? 0 };
    });
    const ok = await confirm({ title: '計画日数を更新しますか？', message: `チェックされた ${body.length} 隻の計画日数を更新します。`, confirmLabel: '更新する' });
    if (!ok) return;
    try {
      await save.mutateAsync({ fiscalYear: year, items: body });
      toast.success('計画日数を更新しました', `${body.length} 隻`);
    } catch (e) {
      if (e instanceof ApiError && Object.keys(e.fieldErrors).length) {
        // items.N.planDaysQk → 行・四半期ごとの表示へ（N は送信した配列の添字）
        const out: Record<string, string> = {};
        for (const [path, message] of Object.entries(e.fieldErrors)) {
          const m = /^items\.(\d+)\.planDaysQ(\d)$/.exec(path);
          const target = m ? targets[Number(m[1])] : undefined;
          if (m && target) out[`${target.vesselId}.${m[2]}`] = message;
        }
        setErrors(out);
        toast.error('更新できませんでした', Object.values(e.fieldErrors)[0] ?? errorMessage(e));
      } else toast.error('更新できませんでした', errorMessage(e));
    }
  };

  const operatorId = watch('businessOperatorId');
  return (
    <>
      <PageHeader
        eyebrow="船舶・計画"
        title="計画日数登録"
        description="参加船ごとの第1〜第4四半期の計画日数を登録します。更新したい船にチェックを付けて「更新」を押してください。"
        actions={<Button icon={<ArrowLeft size={16} />} onClick={() => navigate(yearPath('vessels'))}>名簿一覧へ</Button>}
        meta={limit != null ? <span className="bs-num">年間限度調査日数 {limit} 日</span> : undefined}
      />
      <SearchPanel
        onSubmit={handleSubmit((v) => {
          const next = new URLSearchParams();
          Object.entries(v).forEach(([k, val]) => val && next.set(k, String(val)));
          setParams(next, { replace: true });
        })}
        onReset={() => {
          reset({ businessOperatorId: '', name: '', fishingMethod: '' });
          setParams(new URLSearchParams(), { replace: true });
        }}
        columns={4}
        summary={[filter.businessOperatorId && `事業実施者ID: ${filter.businessOperatorId}`, filter.name && `船名: ${filter.name}`, filter.fishingMethod].filter(Boolean).join(' ／ ') || '条件なし'}
      >
        <FormField label="事業実施者">
          <BusinessOperatorSelect fiscalYear={year} value={operatorId ? Number(operatorId) : null} onChange={(v) => setValue('businessOperatorId', v == null ? '' : String(v))} />
        </FormField>
        <FormField label="船名">
          <TextField {...register('name')} />
        </FormField>
        <FormField label="漁業種類">
          <Select placeholder="すべて" options={(codes?.fishingMethods ?? []).map((v) => ({ value: v, label: v }))} {...register('fishingMethod')} />
        </FormField>
      </SearchPanel>

      <Panel title="四半期ごとの一括入力" description="チェックした船の計画日数をまとめて入力します。">
        <div className="bs-plan-bulk">
          {([1, 2, 3, 4] as Quarter[]).map((q) => (
            <div key={q} className="bs-plan-bulk__cell">
              <div className="bs-plan-bulk__row">
                <NumberField aria-label={`第${q}四半期の一括入力値`} width="xs" unit="日" value={bulk[q]} onChange={(v) => setBulk((b) => ({ ...b, [q]: v }))} />
              </div>
              <Button size="sm" onClick={() => void applyBulk(q)}>第{q}四半期一括入力</Button>
            </div>
          ))}
        </div>
      </Panel>

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isPending ? (
        <Loading block />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState compact title="該当する参加船はありません" description="検索条件を変更してください。" />
        </Panel>
      ) : (
        <div className="bs-table-wrap is-dense">
          <div className="bs-table-meta">
            <div className="bs-table-count"><strong>{rows.length}</strong> 隻<span className="bs-table-selected">{checked.size} 隻選択中</span></div>
            <div className="bs-table-toolbar">
              <Button variant="primary" icon={<Save size={16} />} onClick={onSave} loading={save.isPending} disabled={checked.size === 0}>
                チェックされた漁船の日数を更新
              </Button>
            </div>
          </div>
          <div className="bs-table-scroll">
            <table className="bs-table bs-plan-table">
              <thead>
                <tr>
                  <th className="bs-table__check">
                    <Checkbox aria-label="すべて選択" checked={allChecked} indeterminate={checked.size > 0 && !allChecked} onChange={(e) => setChecked(e.target.checked ? new Set(rows.map((r) => r.vesselId)) : new Set())} />
                  </th>
                  <th className="is-right" style={{ width: 70 }}>名簿No</th>
                  <th style={{ width: 130 }}>事業実施者</th>
                  <th>船名</th>
                  <th style={{ width: 140 }}>漁船登録番号</th>
                  <th style={{ width: 170 }}>漁業種類</th>
                  {[1, 2, 3, 4].map((q) => (
                    <th key={q} className="is-right" style={{ width: 100 }}>第{q}四半期</th>
                  ))}
                  <th className="is-right" style={{ width: 80 }}>合計</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: PlanDaysRow) => {
                  const e = edits[r.vesselId];
                  if (!e) return null;
                  const total = (e.planDaysQ1 ?? 0) + (e.planDaysQ2 ?? 0) + (e.planDaysQ3 ?? 0) + (e.planDaysQ4 ?? 0);
                  return (
                    <tr key={r.vesselId} className={checked.has(r.vesselId) ? 'is-selected' : undefined}>
                      <td className="bs-table__check">
                        <Checkbox aria-label={`${r.name}を選択`} checked={checked.has(r.vesselId)} onChange={(ev) => toggle(r.vesselId, ev.target.checked)} />
                      </td>
                      <td className="is-right bs-num">{r.displayOrder}</td>
                      <td>{r.businessOperatorName}</td>
                      <td><strong>{r.name}</strong>{!r.isApproved && <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 12 }}>未承認</span>}</td>
                      <td className="bs-num">{r.registrationNo}</td>
                      <td>{r.fishingMethods.join('・')}</td>
                      {([1, 2, 3, 4] as Quarter[]).map((q) => {
                        const err = errors[`${r.vesselId}.${q}`];
                        return (
                          <td key={q} className={err ? 'is-right is-error' : 'is-right'}>
                            <NumberField aria-label={`${r.name} 第${q}四半期`} width="full" invalid={!!err} value={e[Q_KEYS[q - 1]!]} onChange={(v) => setQ(r.vesselId, q, v)} />
                            {err && <div className="bs-plan-error">{err}</div>}
                          </td>
                        );
                      })}
                      <td className="is-right bs-num"><strong>{formatNumber(total)}</strong></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
